import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './IncomeFeesReport.css';

function IncomeFeesReport({ onClose }) {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSchool, setFilterSchool] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('school');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [students, filterSchool, searchTerm, sortBy, showActiveOnly]);

  const loadData = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      // Load schools
      const schoolsQuery = query(
        collection(db, 'schools'),
        where('franchiseeRef', '==', franchiseeRef),
        where('isActive', '==', true)
      );
      const schoolsSnapshot = await getDocs(schoolsQuery);
      const schoolsList = schoolsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSchools(schoolsList);

      // Create school lookup map
      const schoolMap = {};
      schoolsList.forEach(school => {
        schoolMap[school.id] = school.schoolName;
      });

      // Load ALL students
      const studentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef)
      );

      const studentsSnapshot = await getDocs(studentsQuery);
      
      const studentsWithPayments = studentsSnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        
        // Get school name from reference
        let schoolName = 'Unknown School';
        if (data.schoolRef) {
          const schoolId = data.schoolRef.id;
          schoolName = schoolMap[schoolId] || 'Unknown School';
        }

        // Get payment data from termPayments
        const termPayments = data.termPayments || {};
        
        return {
          id: docSnap.id,
          studentName: data.studentName || 'Unknown',
          accountNumber: data.accountNumber || 'N/A',
          schoolName: schoolName,
          schoolRef: data.schoolRef,
          isActive: data.isActive !== false,
          // Fee amounts (what they should pay)
          adminFee: data.adminFee || 0,
          termFee: data.feeStructure?.standardFee || 0,
          // Payment amounts (what they have paid)
          adminPaid: termPayments.admin || 0,
          term1Paid: termPayments.term1 || 0,
          term2Paid: termPayments.term2 || 0,
          term3Paid: termPayments.term3 || 0,
          term4Paid: termPayments.term4 || 0,
          // Total fees received (EXCLUDING admin fee - only term fees)
          totalFeesReceived: (termPayments.term1 || 0) + 
                     (termPayments.term2 || 0) + 
                     (termPayments.term3 || 0) + 
                     (termPayments.term4 || 0),
          // Balance
          balance: data.balance || 0
        };
      });

      setStudents(studentsWithPayments);
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading income fees report');
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...students];

    // Active filter
    if (showActiveOnly) {
      filtered = filtered.filter(s => s.isActive);
    }

    // School filter
    if (filterSchool !== 'all') {
      filtered = filtered.filter(s => s.schoolRef?.id === filterSchool);
    }

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(s => {
        const name = s.studentName?.toLowerCase() || '';
        const account = s.accountNumber?.toLowerCase() || '';
        return name.includes(search) || account.includes(search);
      });
    }

    // Sort
    switch (sortBy) {
      case 'school':
        filtered.sort((a, b) => {
          const schoolCompare = (a.schoolName || '').localeCompare(b.schoolName || '');
          if (schoolCompare !== 0) return schoolCompare;
          return (a.studentName || '').localeCompare(b.studentName || '');
        });
        break;
      case 'name-asc':
        filtered.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));
        break;
      case 'name-desc':
        filtered.sort((a, b) => (b.studentName || '').localeCompare(a.studentName || ''));
        break;
      case 'total-desc':
        filtered.sort((a, b) => b.totalFeesReceived - a.totalFeesReceived);
        break;
      case 'total-asc':
        filtered.sort((a, b) => a.totalFeesReceived - b.totalFeesReceived);
        break;
      default:
        break;
    }

    setFilteredStudents(filtered);
  };

  const formatCurrency = (amount) => {
    return `R${(amount || 0).toFixed(2)}`;
  };

  // Calculate totals
  const getTotals = () => {
    return filteredStudents.reduce((totals, s) => ({
      adminPaid: totals.adminPaid + s.adminPaid,
      term1Paid: totals.term1Paid + s.term1Paid,
      term2Paid: totals.term2Paid + s.term2Paid,
      term3Paid: totals.term3Paid + s.term3Paid,
      term4Paid: totals.term4Paid + s.term4Paid,
      totalFeesReceived: totals.totalFeesReceived + s.totalFeesReceived
    }), {
      adminPaid: 0,
      term1Paid: 0,
      term2Paid: 0,
      term3Paid: 0,
      term4Paid: 0,
      totalFeesReceived: 0
    });
  };

  const exportToCSV = () => {
    const headers = ['School', 'Student Name', 'Account #', 'Status', 'Admin Fee', 'Term 1', 'Term 2', 'Term 3', 'Term 4', 'Total Fees Received'];
    
    const rows = filteredStudents.map(student => [
      student.schoolName || 'N/A',
      student.studentName || 'N/A',
      student.accountNumber || 'N/A',
      student.isActive ? 'Active' : 'Inactive',
      student.adminPaid.toFixed(2),
      student.term1Paid.toFixed(2),
      student.term2Paid.toFixed(2),
      student.term3Paid.toFixed(2),
      student.term4Paid.toFixed(2),
      student.totalFeesReceived.toFixed(2)
    ]);

    // Add totals row
    const totals = getTotals();
    rows.push([
      'TOTALS',
      '',
      '',
      '',
      totals.adminPaid.toFixed(2),
      totals.term1Paid.toFixed(2),
      totals.term2Paid.toFixed(2),
      totals.term3Paid.toFixed(2),
      totals.term4Paid.toFixed(2),
      totals.totalFeesReceived.toFixed(2)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `income-fees-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading income fees report...</div>;
  }

  const totals = getTotals();

  return (
    <div className="income-fees-container">
      <div className="report-header">
        <div className="header-top">
          <h1>{'\u{1F4B0}'} Income Fees Report</h1>
        </div>

        <button
          onClick={() => setFiltersCollapsed(!filtersCollapsed)}
          className="toggle-filters-btn"
        >
          {filtersCollapsed ? '\u25BC Show Filters & Summary' : '\u25B2 Hide Filters & Summary'}
        </button>

        {!filtersCollapsed && (
          <>
            <div className="filters-container">
              <div className="filter-group">
                <label>School:</label>
                <select
                  value={filterSchool}
                  onChange={(e) => setFilterSchool(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Schools</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.id}>
                      {school.schoolName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Sort By:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="filter-select"
                >
                  <option value="school">School, then Name</option>
                  <option value="name-asc">Name (A-Z)</option>
                  <option value="name-desc">Name (Z-A)</option>
                  <option value="total-desc">Highest Fees First</option>
                  <option value="total-asc">Lowest Fees First</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Status:</label>
                <select
                  value={showActiveOnly ? 'active' : 'all'}
                  onChange={(e) => setShowActiveOnly(e.target.value === 'active')}
                  className="filter-select"
                >
                  <option value="active">Active Students Only</option>
                  <option value="all">All Students</option>
                </select>
              </div>

              <div className="filter-group search-group">
                <label>Search:</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name or Account #..."
                  className="search-input"
                />
              </div>

              <div className="filter-group">
                <label>&nbsp;</label>
                <button onClick={exportToCSV} className="btn-export">
                  {'\u{1F4BE}'} Export to CSV
                </button>
              </div>
            </div>

            <div className="summary-bar">
              <div className="summary-item">
                <span className="summary-label">Total Students:</span>
                <span className="summary-value">{filteredStudents.length}</span>
              </div>
              <div className="summary-item highlight">
                <span className="summary-label">Total Fees Received:</span>
                <span className="summary-value">{formatCurrency(totals.totalFeesReceived)}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {filteredStudents.length === 0 ? (
        <div className="no-results">
          <div className="no-results-icon">{'\u{1F50D}'}</div>
          <h2>No Students Found</h2>
          <p>No students match your current filters.</p>
        </div>
      ) : (
        <div className="report-table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>School</th>
                <th>Student Name</th>
                <th>Account #</th>
                <th className="amount-header">Admin Fee</th>
                <th className="amount-header">Term 1</th>
                <th className="amount-header">Term 2</th>
                <th className="amount-header">Term 3</th>
                <th className="amount-header">Term 4</th>
                <th className="amount-header total-header">Total Fees Received</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student, index) => {
                // Check if this is a new school (for grouping visual)
                const isNewSchool = index === 0 || 
                  filteredStudents[index - 1].schoolName !== student.schoolName;
                
                return (
                  <tr 
                    key={student.id} 
                    className={`${!student.isActive ? 'inactive-row' : ''} ${isNewSchool && sortBy === 'school' ? 'new-school-row' : ''}`}
                  >
                    <td className="school-cell">
                      {sortBy === 'school' && !isNewSchool ? '' : student.schoolName}
                    </td>
                    <td>
                      <div className="student-name-cell">
                        {student.studentName}
                        {!student.isActive && (
                          <span className="inactive-badge">Inactive</span>
                        )}
                      </div>
                    </td>
                    <td className="account-cell">{student.accountNumber}</td>
                    <td className={`amount-cell ${student.adminPaid > 0 ? 'paid' : 'zero'}`}>
                      {formatCurrency(student.adminPaid)}
                    </td>
                    <td className={`amount-cell ${student.term1Paid > 0 ? 'paid' : 'zero'}`}>
                      {formatCurrency(student.term1Paid)}
                    </td>
                    <td className={`amount-cell ${student.term2Paid > 0 ? 'paid' : 'zero'}`}>
                      {formatCurrency(student.term2Paid)}
                    </td>
                    <td className={`amount-cell ${student.term3Paid > 0 ? 'paid' : 'zero'}`}>
                      {formatCurrency(student.term3Paid)}
                    </td>
                    <td className={`amount-cell ${student.term4Paid > 0 ? 'paid' : 'zero'}`}>
                      {formatCurrency(student.term4Paid)}
                    </td>
                    <td className={`amount-cell total-cell ${student.totalFeesReceived > 0 ? 'paid' : 'zero'}`}>
                      {formatCurrency(student.totalFeesReceived)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td colSpan="3"><strong>TOTALS:</strong></td>
                <td className="amount-cell paid"><strong>{formatCurrency(totals.adminPaid)}</strong></td>
                <td className="amount-cell paid"><strong>{formatCurrency(totals.term1Paid)}</strong></td>
                <td className="amount-cell paid"><strong>{formatCurrency(totals.term2Paid)}</strong></td>
                <td className="amount-cell paid"><strong>{formatCurrency(totals.term3Paid)}</strong></td>
                <td className="amount-cell paid"><strong>{formatCurrency(totals.term4Paid)}</strong></td>
                <td className="amount-cell total-cell paid"><strong>{formatCurrency(totals.totalFeesReceived)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

export default IncomeFeesReport;